import { useK2Setup } from '@/eshop/hooks/useK2Setup'
import { searchProducts } from '@/eshop/queries'
import { useQuery } from '@tanstack/react-query'
import {
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import Loader from '../Loader'
import {
    Product,
    ProductGroup,
    ProductsConnectionEdge,
} from '@/eshop/gql/graphql'
import Pagination from '../Pagination'
import { ProdutListItem } from './ListItem'
import { ProductListLetter } from '../ProductList'
import { getFirstL, shouldRenderFirstLetter } from './utils'
import clsx from 'clsx'
import { EshopContext } from '../EshopProvider'

interface Props {
    search: string
    className?: string
}
const PAGE_SIZE = 20

interface DataItem {
    group: ProductGroup
    products: ProductsConnectionEdge[]
}

type Data = DataItem[]

export function SearchProductList({ search, className }: Props) {
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(0)
    const k2 = useK2Setup()
    const [data, setData] = useState<Data>([])
    const [openRow, setOpenRow] = useState<string[]>([])

    const { categories } = useContext(EshopContext)
    const selectedCategoryId = useMemo(() => {
        return categories.reduce<string | null>(
            (acc, cat) => cat?.id ?? acc,
            null
        )
    }, [categories])

    const handleItemClick = useCallback(
        (pid: Product['id']) => () =>
            setOpenRow((state) =>
                state.includes(pid)
                    ? state.filter((id) => id !== pid)
                    : [...state, pid]
            ),
        []
    )

    const { data: rawSearchData, isFetching: isSearchFetching } = useQuery({
        queryKey: ['product-groups', 'search', search],
        queryFn: () =>
            searchProducts()({
                term: { eq: search },
                languageId: { eq: k2.languageId },
            }).toPromise(),
        enabled: !!search,
    })
    const searchData = useMemo(
        () =>
            rawSearchData && rawSearchData.match
                ? rawSearchData.match({
                      Ok: (r) => r.searchProducts,
                      Error: () => undefined,
                  })
                : undefined,
        [rawSearchData]
    )

    //reset page after search changes
    useEffect(() => {
        if (isSearchFetching) {
            setData([])
        }
        // setPage(1)
        if (!rawSearchData) {
            return
        }

        const productsForGroup: Record<string, ProductsConnectionEdge[]> = {}
        const groups: Array<DataItem['group']> =
            rawSearchData?.match?.({
                Ok: (r) => {
                    const g: Array<DataItem['group']> = []
                    r.searchProducts.edges?.forEach((e) => {
                        const pg = e.node.productGroup
                        if (!pg) {
                            return
                        }

                        if (!productsForGroup[pg.id]) {
                            productsForGroup[pg.id] = [e]
                        } else {
                            productsForGroup[pg.id].push(e)
                        }

                        if (pg && !g.find((gr) => gr.id === pg.id)) {
                            g.push(pg)
                        }
                    })
                    return g
                },
                Error: () => [],
            }) || []
        setData(
            groups.map((g) => ({
                group: g,
                products: productsForGroup[g.id] || [],
            }))
        )
    }, [search, rawSearchData, isSearchFetching])

    return (
        <div className={clsx(className, 'mb-16')}>
            <Loader isLoading={isSearchFetching}>
                {data.map((item, i, arr) => {
                    const firstLetter = getFirstL(
                        item.group.sortingNameLang,
                        k2.languageId === 5
                    )
                    return (
                        <Fragment key={item.group.id}>
                            {(i === 0 ||
                                shouldRenderFirstLetter(
                                    item.group.sortingNameLang,
                                    arr[i - 1]?.group?.sortingNameLang,
                                    i,
                                    k2.languageId === 5
                                )) && (
                                <ProductListLetter>
                                    {firstLetter}
                                </ProductListLetter>
                            )}
                            <div>
                                <ProdutListItem
                                    selectedCategoryId={null}
                                    item={item.group}
                                    isOpen={openRow.includes(item.group.id)}
                                    onClick={handleItemClick(item.group.id)}
                                    // search is not sorted correctly, so we ignore products from that for now
                                    // groupProducts={item.products}
                                />
                            </div>
                        </Fragment>
                    )
                })}
            </Loader>
            {page > 0 && total > 0 && (
                <Pagination
                    onPageChange={setPage}
                    current={page}
                    total={total}
                    className="mt-24"
                />
            )}
        </div>
    )
}
